/**
 * Host implementation of upstream `lib/api/pro-api/pro-api` — see
 * ../../../../README.md.
 *
 * Same three methods the vendored tree calls, same return shapes (axios
 * responses), built on this repo's ProAPI axios instance so the base URL,
 * interceptors and `NEXT_PUBLIC_PRO_API_HOST` are the ones the rest of the app
 * already uses. The URL itself is built by the vendored `pricingPlansPath`, so
 * the catalogue version / country group / ref rules cannot drift from upstream.
 */

import {
  ProAPI as LegacyProAPI,
  getCreditsDisplayScale,
} from '@lib/ProAPI/pro.api';

import { pricingPlansPath } from 'lib/api/pro-api/pricingPlansPath';

import type { CountryGroup, ExperimentBucket } from 'helpers/experiment';
import type { InfluencerReferralReceipt } from 'lib/influencer-referral/shared';
import type { RawPricingAPIResponse } from 'types/pricingV2.types';

interface InfluencerCampaignResponse {
  ok?: {
    campaign_id: string;
    influencer_id: string;
    is_super: boolean;
    referral?: InfluencerReferralReceipt;
  };
}

class ProAPIImpl {
  pricingPlans(
    locale?: string,
    bucket?: ExperimentBucket | null,
    countryGroup?: CountryGroup | null,
    referral?: string | null,
    country?: string | null,
  ) {
    return LegacyProAPI.get<RawPricingAPIResponse>(
      pricingPlansPath(locale, bucket, countryGroup, referral, country),
    );
  }

  influencerCampaign(campaignCode: string) {
    return LegacyProAPI.get<InfluencerCampaignResponse>(
      '/api/influencer_campaign',
      { params: { campaign_code: campaignCode } },
    );
  }

  /**
   * Resolves rather than rejects, and is memoised for a minute — both
   * guarantees come from this repo's own implementation, which matches
   * upstream's.
   */
  creditsDisplayScale(): Promise<number> {
    return getCreditsDisplayScale();
  }
}

const ProAPI = new ProAPIImpl();

export { ProAPI };
